import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Timer, X } from "lucide-react";
import { useApp } from "@/context/AppContext";

const LETTERS = ["A", "B", "C", "D"];

export default function QuizPlay() {
  const navigate = useNavigate();
  const { session, finishQuiz } = useApp();

  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [timeLeft, setTimeLeft] = useState(session ? session.timer : 15);
  const tickRef = useRef(null);

  const q = session?.questions[idx];

  const next = useCallback((finalScore) => {
    if (idx + 1 >= session.questions.length) {
      const earned = finishQuiz({
        category: session.category,
        categoryId: session.categoryId,
        difficulty: session.difficulty,
        score: finalScore,
        total: session.questions.length,
        venue: session.venue,
        prize: session.prize || null,
        venueId: session.venueId || null,
      });
      navigate("/results", { state: { newlyEarned: earned } });
    } else {
      setIdx((i) => i + 1);
      setSelected(null);
      setLocked(false);
      setTimeLeft(session.timer);
    }
  }, [idx, session, finishQuiz, navigate]);

  const handleAnswer = useCallback((choice) => {
    if (locked) return;
    clearInterval(tickRef.current);
    setLocked(true);
    setSelected(choice);
    const correct = choice === q.answer_index;
    const newScore = correct ? score + 1 : score;
    if (correct) setScore(newScore);
    setTimeout(() => next(newScore), 1100);
  }, [locked, q, score, next]);

  // countdown
  useEffect(() => {
    if (!session || locked) return;
    tickRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(tickRef.current);
          setLocked(true);
          setSelected(-1);
          setTimeout(() => next(score), 1100);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [idx, session, locked, next, score]);

  if (!session) {
    navigate("/setup");
    return null;
  }

  const pct = (timeLeft / session.timer) * 100;
  const danger = timeLeft <= 5;

  const tileClass = (i) => {
    if (!locked) return "bg-slate-800 border-slate-700 text-slate-100";
    if (i === q.answer_index) return "bg-green-500 border-green-400 text-white";
    if (i === selected) return "bg-red-500 border-red-400 text-white";
    return "bg-slate-800 border-slate-700 text-slate-500 opacity-60";
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-slate-950 p-6 flex flex-col">
      <div className="flex items-center justify-between pt-2 mb-4">
        <button data-testid="quiz-quit-btn" onClick={() => navigate("/")} className="text-slate-500 p-1">
          <X strokeWidth={2.5} />
        </button>
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-amber-400">{session.category}</span>
        <span data-testid="quiz-score" className="font-display font-black text-slate-50">{score} pts</span>
      </div>

      <div className="flex items-center gap-3 mb-2">
        <Timer size={18} className={danger ? "text-red-400" : "text-cyan-400"} />
        <div className="flex-1 h-3 bg-slate-800 rounded-full overflow-hidden">
          <motion.div
            className={danger ? "h-full bg-red-500" : "h-full bg-cyan-400"}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.9, ease: "linear" }}
          />
        </div>
        <span data-testid="quiz-timer" className={`font-display font-black w-8 text-right ${danger ? "text-red-400" : "text-slate-50"}`}>{timeLeft}</span>
      </div>

      <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-6">
        Question {idx + 1} of {session.questions.length}
      </p>

      <AnimatePresence mode="wait">
        <motion.h2
          key={idx}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          data-testid="quiz-question"
          className="font-display text-2xl font-black text-slate-50 leading-tight mb-8"
        >
          {q.question}
        </motion.h2>
      </AnimatePresence>

      <div className="space-y-3">
        {q.options.map((opt, i) => (
          <motion.button
            key={`${idx}-${opt}`}
            data-testid={`quiz-answer-${LETTERS[i]}`}
            whileTap={locked ? {} : { scale: 0.97 }}
            onClick={() => handleAnswer(i)}
            disabled={locked}
            className={`w-full text-left rounded-2xl p-5 border-2 flex items-center gap-4 no-tap-highlight ${tileClass(i)}`}
          >
            <span className="font-display font-black text-lg w-8 h-8 flex items-center justify-center rounded-lg bg-slate-950/25 shrink-0">
              {LETTERS[i]}
            </span>
            <span className="font-bold">{opt}</span>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
